import { supabase } from "@/shared/lib/supabaseClient";
import type { Database, EmprestimoStatus } from "@/shared/types/database.types";

// Estreitado na origem (não é o Row bruto): a coluna `status` usa CHECK
// constraint no Postgres, então o gerador do Supabase a tipa como `string`
// solto. Sobrescrevemos aqui para `EmprestimoStatus` para que qualquer
// consumidor deste tipo (telas, Record<EmprestimoStatus, ...>, etc.) tenha
// a tipagem estrita sem precisar de cast pontual em cada ponto de uso.
export type Emprestimo = Omit<Database["public"]["Tables"]["emprestimos"]["Row"], "status"> & {
  status: EmprestimoStatus;
};

/** Empréstimo com nome do usuário e título do livro resolvidos via JOIN. */
export interface EmprestimoDetalhado extends Emprestimo {
  usuario_nome: string;
  livro_titulo: string;
}

export const emprestimosService = {
  /**
   * Lista todos os empréstimos (visão do admin), com nome da pessoa e
   * título do livro via JOIN. Fase 6 (etapa 6.2): o JOIN de nome passou de
   * `perfis!usuario_id` para `pessoas!pessoa_id` — a coluna `pessoa_id`
   * existe desde a 0018 e está 100% preenchida desde a migration 0021.
   */
  async listarTodos(): Promise<EmprestimoDetalhado[]> {
    const { data, error } = await supabase
      .from("emprestimos")
      .select("*, pessoas!pessoa_id(nome_completo), livros(nome_livro)")
      .order("created_at", { ascending: false });

    if (error) throw new Error("Não foi possível carregar a lista de empréstimos.");

    const typedData = (data || []) as unknown as Array<
      Emprestimo & {
        pessoas: { nome_completo: string } | null;
        livros: { nome_livro: string } | null;
      }
    >;

    return typedData.map((e) => ({
      ...e,
      usuario_nome: e.pessoas?.nome_completo ?? "Pessoa removida",
      livro_titulo: e.livros?.nome_livro ?? "Livro removido",
    }));
  },

  /**
   * Lista os empréstimos da pessoa logada (visão do usuário comum) — não
   * precisa de JOIN com pessoas (é sempre a própria pessoa), mas mantém o
   * JOIN com livros para mostrar o título. Fase 6: o filtro é por
   * `pessoa_id` (`pessoa.id` do Núcleo), não mais por `usuario_id`.
   */
  async listarMeusEmprestimos(pessoaId: string): Promise<EmprestimoDetalhado[]> {
    const { data, error } = await supabase
      .from("emprestimos")
      .select("*, livros(nome_livro)")
      .eq("pessoa_id", pessoaId)
      .order("created_at", { ascending: false });

    if (error) throw new Error("Não foi possível carregar seus empréstimos.");

    const typedData = (data || []) as unknown as Array<
      Emprestimo & { livros: { nome_livro: string } | null }
    >;

    return typedData.map((e) => ({
      ...e,
      usuario_nome: "",
      livro_titulo: e.livros?.nome_livro ?? "Livro removido",
    }));
  },

  /**
   * Solicita o empréstimo e retorna quantas OUTRAS pessoas já têm
   * solicitação pendente para o mesmo livro — usado para avisar o usuário
   * de que a fila já existe, evitando falsa expectativa de aprovação rápida.
   */
  async solicitar(livroId: string): Promise<{ outrasSolicitacoesPendentes: number }> {
    const { error } = await supabase.rpc("solicitar_emprestimo", { p_livro_id: livroId });

    if (error) {
      if (error.message?.includes("andamento")) {
        throw new Error("Você já possui um empréstimo em andamento para este livro.");
      }
      if (error.message?.includes("indisponível")) {
        throw new Error("Este livro não está mais disponível para empréstimo.");
      }
      throw new Error("Não foi possível solicitar o empréstimo.");
    }

    const { data: contagem } = await supabase.rpc("contar_outras_solicitacoes_pendentes", {
      p_livro_id: livroId,
    });

    return { outrasSolicitacoesPendentes: typeof contagem === "number" ? contagem : 0 };
  },

  async aprovar(emprestimoId: string, diasPrazo = 14): Promise<void> {
    const { error } = await supabase.rpc("aprovar_emprestimo", {
      p_emprestimo_id: emprestimoId,
      p_dias_prazo: diasPrazo,
    });

    if (error) {
      if (error.message?.includes("Não há exemplares disponíveis")) {
        throw new Error(error.message);
      }
      throw new Error("Não foi possível aprovar o empréstimo.");
    }
  },

  async rejeitar(emprestimoId: string, motivo?: string): Promise<void> {
    const { error } = await supabase.rpc("rejeitar_emprestimo", {
      p_emprestimo_id: emprestimoId,
      p_motivo: motivo ?? undefined,
    });

    if (error) throw new Error("Não foi possível recusar o empréstimo.");
  },

  async registrarDevolucao(emprestimoId: string): Promise<void> {
    const { error } = await supabase.rpc("registrar_devolucao", {
      p_emprestimo_id: emprestimoId,
    });

    if (error) throw new Error("Não foi possível registrar a devolução.");
  },

  /**
   * Usuário dispensa o aviso de empréstimo recusado. Depois disso, esse
   * empréstimo específico não aparece mais no banner de recusados.
   */
  async marcarRejeicaoVisualizada(emprestimoId: string): Promise<void> {
    const { error } = await supabase.rpc("marcar_rejeicao_visualizada", {
      p_emprestimo_id: emprestimoId,
    });

    if (error) throw new Error("Não foi possível dispensar o aviso.");
  },
};

export const STATUS_LABEL: Record<EmprestimoStatus, string> = {
  PENDENTE: "Pendente",
  EM_ABERTO: "Em andamento",
  DEVOLVIDO: "Devolvido",
  ATRASADO: "Atrasado",
  REJEITADO: "Recusado",
};
